import { FC, useState, useMemo, useEffect, useContext, useCallback } from 'react';
import { Icon, Select, SelectOptionType, Input, Button, TextArea, Switch, Refreshing, Alert, useToast } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../../Localization';
import InterfaceContext, { ConfigurationContext, LoggedInConfigurationContext } from '../../../../Context';
import { safeParse } from '../../../../Util';
import { InfoDict } from '../../../../Questions/Props';
import { Message, SupportCaseInfoResponse } from '../../../Support/types';
import { usePost } from '../../../../API';
import { useDebouncedCallback } from '../../../../Hooks/Debounce';
import { ContentTemplate } from '@aidkitorg/types/lib/survey';
import { CommsContentTemplate } from '../shared/types';
import {
  getContactInfoWithFallback,
  checkCanSendWhatsApp,
  getAvailableChannels
} from '../shared/utils';
import {
  useWhatsAppWindow,
  useContactSelector,
  useInitialChannelSelection
} from '../shared/hooks';

interface ReplyBoxProps {
  onSendMessage: (
    message: string,
    channel: 'email' | 'sms' | 'whatsapp',
    sendAt?: Date,
    contactKeys?: { nameKey: string; phoneKey: string; emailKey: string },
    contentTemplate?: ContentTemplate
  ) => Promise<void>;
  applicantInfo?: InfoDict;
  caseInfo: SupportCaseInfoResponse['supportCase'];
  messages: Message[];
}

export const ReplyBox: FC<ReplyBoxProps> = ({ onSendMessage, applicantInfo, caseInfo, messages }) => {
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);
  const context = useContext(InterfaceContext);
  const loggedInConfig = useContext(LoggedInConfigurationContext);

  // Create fallback contact info using shared utility
  const contactInfo = useMemo(() =>
    getContactInfoWithFallback(applicantInfo, caseInfo.contact),
  [applicantInfo, caseInfo.contact]
  );

  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [whatsAppKey] = useState("whatsapp");
  const [isScheduleMode, setIsScheduleMode] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [messagePreview, setMessagePreview] = useState<string | null>(null);
  const [loadingMessagePreview, setLoadingMessagePreview] = useState(false);

  // WhatsApp template selection state
  const [selectedTemplate, setSelectedTemplate] = useState<CommsContentTemplate | undefined>(undefined);
  const [templateMessage, setTemplateMessage] = useState<string | undefined>(undefined);

  // Get WhatsApp content templates from config
  const contentTemplates = useMemo(() => {
    // Get templates from the support channel or default channel
    const templates = caseInfo.channel && caseInfo.channel !== 'default'
      ? loggedInConfig?.comms?.otherChannels?.find((c: any) => c.channelName === caseInfo.channel)?.contentTemplates
      : loggedInConfig?.comms?.default?.contentTemplates;
    return templates as CommsContentTemplate[] | undefined;
  }, [loggedInConfig, caseInfo.channel]);

  const simulateEmail = usePost('/program/admin/simulate_email');
  const simulateNotifications = usePost('/program/admin/simulate_notifications');
  const { toast } = useToast();

  // Use custom hooks from shared
  const canSendWhatsApp = useMemo(() =>
    checkCanSendWhatsApp(whatsAppKey, contactInfo, messages),
  [whatsAppKey, contactInfo, messages]
  );

  const whatsAppWindowClosed = useWhatsAppWindow(canSendWhatsApp, messages);

  const alternateContacts = useMemo(() =>
    safeParse(config.alternate_contact || '[]', []),
  [config.alternate_contact]
  );

  const availableChannels = useMemo(() =>
    getAvailableChannels(contactInfo, canSendWhatsApp, alternateContacts),
  [contactInfo, canSendWhatsApp, alternateContacts]
  );

  const { selectedChannel, setSelectedChannel } = useInitialChannelSelection(
    messages,
    availableChannels,
    L
  );

  const {
    contactPhoneKey,
    contactEmailKey,
    contactNameKey,
    contactSelectorOptions,
    showContactSelector
  } = useContactSelector(config, contactInfo, selectedChannel, context.lang);

  // Callback to handle template selection and simulation
  const selectTemplateCallback = useCallback(async (template: CommsContentTemplate | undefined) => {
    setSelectedTemplate(template);
    setTemplateMessage(undefined);

    if (!applicantInfo?.uid || !template?.body) return;

    try {
      const simulated = await simulateNotifications({
        uids: [applicantInfo.uid],
        emailKey: contactEmailKey,
        phoneKey: contactPhoneKey,
        content: template.body
      });

      const messageWeCareAbout = simulated?.[applicantInfo.uid]?.[0];
      if (messageWeCareAbout && 'error' in messageWeCareAbout) {
        toast({
          description: L.applicant.comms.error_simulating_notification_prefix + messageWeCareAbout.error,
          variant: 'error'
        });
      } else if (messageWeCareAbout) {
        setTemplateMessage(messageWeCareAbout.smsContent);
      }
    } catch (error) {
      toast({
        description: L.applicant.comms.error_simulating_template,
        variant: 'error'
      });
    }
  }, [applicantInfo?.uid, contactEmailKey, contactPhoneKey, simulateNotifications, toast, L.applicant.comms]);

  // Debounced email preview simulation
  const simulateEmailDebounced = useDebouncedCallback(async (message: string) => {
    try {
      setMessagePreview(await simulateEmail({ message }));
    } finally {
      setLoadingMessagePreview(false);
    }
  }, 750, []);


  // Create select options for channels
  const channelOptions: SelectOptionType[] = useMemo(() => {
    return availableChannels.map(channel => ({
      label: channel === 'email'
        ? L.applicant.comms.send_email
        : channel === 'whatsapp'
          ? L.applicant.comms.send_whatsapp
          : L.applicant.comms.send_text,
      value: channel,
    }));
  }, [availableChannels, L.applicant.comms]);

  // Create select options for WhatsApp templates
  const templateOptions: SelectOptionType[] = useMemo(() => {
    if (!contentTemplates) return [];
    return contentTemplates.map(template => ({
      label: template.displayName[context.lang] || template.displayName['en'] || template.name,
      value: template.name,
    }));
  }, [contentTemplates, context.lang]);


  // Get recipient display based on selected channel and contact
  const recipientDisplay = useMemo(() => {
    if (!selectedChannel) return '';

    const channel = selectedChannel.value as 'email' | 'sms' | 'whatsapp';

    if (channel === 'email') {
      return contactInfo?.[contactEmailKey] || '';
    } else {
      // For SMS and WhatsApp, show phone number from selected contact
      return contactInfo?.[contactPhoneKey] || '';
    }
  }, [selectedChannel, contactInfo, contactPhoneKey, contactEmailKey]);

  // Determine if scheduling is allowed (not allowed for email)
  const canSchedule = selectedChannel?.value !== 'email';

  // Clear scheduled time and exit schedule mode when switching to email
  useEffect(() => {
    if (selectedChannel?.value === 'email') {
      setScheduledDateTime('');
      setIsScheduleMode(false);
    }
  }, [selectedChannel]);

  // Clear template when switching away from WhatsApp
  useEffect(() => {
    if (selectedChannel?.value !== 'whatsapp') {
      setSelectedTemplate(undefined);
      setTemplateMessage(undefined);
    }
  }, [selectedChannel]);


  // Update preview when message or selected channel changes
  useEffect(() => {
    if (selectedChannel?.value === 'email' && showPreview && replyMessage) {
      setLoadingMessagePreview(true);
      simulateEmailDebounced(replyMessage);
    }
  }, [selectedChannel, showPreview, replyMessage]);

  const handleSend = async () => {
    // Check if we have either a message or a template message
    const messageToSend = selectedTemplate && templateMessage ? templateMessage : replyMessage;
    if (!messageToSend.trim() || !selectedChannel) return;

    setSending(true);
    try {
      const sendAt = scheduledDateTime ? new Date(scheduledDateTime) : undefined;
      const contactKeys = {
        nameKey: contactNameKey,
        phoneKey: contactPhoneKey,
        emailKey: contactEmailKey,
      };
      await onSendMessage(
        messageToSend,
        selectedChannel.value as 'email' | 'sms' | 'whatsapp',
        sendAt,
        contactKeys,
        selectedTemplate?.templates
      );
      setReplyMessage('');
      setTemplateMessage(undefined);
      setSelectedTemplate(undefined);
      setScheduledDateTime('');
      setIsScheduleMode(false);
    } finally {
      setSending(false);
    }
  };

  const handleScheduleClick = () => {
    setIsScheduleMode(!isScheduleMode);
    if (isScheduleMode) {
      // If disabling schedule mode, clear the scheduled date
      setScheduledDateTime('');
    }
  };

  return (
    <div className="bg-white border-t-2">
      {/* To: Channel Selector - Above everything */}
      {channelOptions.length > 0 && (
        <>
          <div className="p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-40">
                  <Select
                    label={L.applicant.comms.channel}
                    hideLabel={true}
                    description={L.applicant.comms.select_communication_channel}
                    value={selectedChannel}
                    onChange={setSelectedChannel}
                    options={channelOptions}
                    disabled={channelOptions.length === 1 || sending}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {L.applicant.comms.to.replace('$to', recipientDisplay)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {/* Contact Selector - shows when there are alternate contacts */}
                {showContactSelector && (
                  <div className="w-48">
                    <Select
                      label={L.applicant.comms.contact_label}
                      labelLocation="inline"
                      description={L.applicant.comms.select_contact_description}
                      value={(() => {
                        // Construct current value identifier from state
                        const currentValueId = `${contactPhoneKey}:${contactEmailKey}:${contactNameKey}`;
                        const matchedOption = contactSelectorOptions.find(opt => opt.value === currentValueId);
                        return matchedOption ? {
                          label: matchedOption.displayName, // Use displayName for selected value
                          value: matchedOption.value
                        } : {
                          label: contactSelectorOptions[0]?.displayName || L.applicant.comms.applicant_fallback,
                          value: contactSelectorOptions[0]?.value || 'phone_number:email:legal_name'
                        };
                      })()}
                      onChange={(selected) => {
                        const option = contactSelectorOptions.find(opt => opt.value === selected?.value);
                        if (option?.callback && !option.disabled) {
                          option.callback();
                        }
                      }}
                      options={contactSelectorOptions
                        .filter(opt => !opt.disabled)
                        .map(opt => ({
                          label: opt.displayName, // Use displayName so selected value shows correctly
                          value: opt.value,
                          description: opt.dropdownLabel !== opt.displayName ? opt.dropdownLabel : undefined, // Show full label as description
                        }))}
                      disabled={sending || contactSelectorOptions.filter(opt => !opt.disabled).length <= 1}
                    />
                  </div>
                )}
                {/* Show Preview Toggle - only for email */}
                {selectedChannel?.value === 'email' && (
                  <div className="flex items-center">
                    <Switch
                      label={L.applicant.comms.show_preview}
                      checked={showPreview}
                      onCheckedChange={setShowPreview}
                      size="sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Divider with margins */}
          <div className="px-3">
            <div className="border-b border-gray-200"></div>
          </div>
        </>
      )}

      <div className="p-3 space-y-3">
        {/* Label - consistent for both modes */}
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-gray-700 self-end">{L.applicant.comms.reply_to_this_case}</div>
          {/* WhatsApp Template Selector */}
          {selectedChannel?.value === 'whatsapp' && contentTemplates && contentTemplates.length > 0 && (
            <div className="w-48">
              <Select
                label={L.applicant.comms.select_template}
                hideLabel={true}
                description={L.applicant.comms.select_template}
                value={selectedTemplate ? {
                  label: selectedTemplate.displayName[context.lang] || selectedTemplate.displayName['en'] || selectedTemplate.name,
                  value: selectedTemplate.name,
                } : undefined}
                onChange={async (option) => {
                  if (option) {
                    const template = contentTemplates.find(t => t.name === option.value);
                    await selectTemplateCallback(template);
                  } else {
                    await selectTemplateCallback(undefined);
                  }
                }}
                options={templateOptions}
                placeholder={L.applicant.comms.select_template}
                disabled={sending}
              />
            </div>
          )}
        </div>

        {/* Fixed height container to prevent height changes when toggling preview */}
        <div className="h-24">
          {/* Show textarea when NOT in preview mode, or show preview when in preview mode */}
          {selectedTemplate ? (
            /* WhatsApp Template Message - Shows when template is selected */
            <TextArea
              label={L.applicant.comms.reply_to_this_case}
              showLabel={false}
              value={templateMessage || ''}
              onChange={() => {}} // Disabled, no changes allowed
              placeholder={L.applicant.comms.type_your_message_here}
              disabled={true}
              className="resize-none h-full"
            />
          ) : !(showPreview && selectedChannel?.value === 'email') ? (
            <TextArea
              label={L.applicant.comms.reply_to_this_case}
              showLabel={false}
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder={L.applicant.comms.type_your_message_here}
              disabled={sending}
              className="resize-none h-full"
            />
          ) : (
            /* Email Preview - Shows when preview is enabled and email is selected */
            <Refreshing loading={loadingMessagePreview} className="h-full">
              <div
                className="h-full border border-gray-300 rounded-xl px-3 py-2 markdown overflow-x-auto overflow-y-auto whitespace-normal bg-gray-50"
                dangerouslySetInnerHTML={{ __html: messagePreview || `<p class="text-gray-400 italic">${L.applicant.comms.no_preview_available}</p>` }}
              />
            </Refreshing>
          )}
        </div>

        {/* Schedule DateTime Input - Only show when schedule mode is active */}
        {isScheduleMode && (
          <div className="w-full">
            <Input
              type="datetime-local"
              label={L.applicant.comms.schedule_send_time}
              value={scheduledDateTime}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              disabled={sending}
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {L.applicant.comms.messages_will_automatically_refresh}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleScheduleClick}
              variant={isScheduleMode ? "primary" : "secondary"}
              size="md"
              icon={<Icon name="CalendarIcon" className="h-4 w-4" />}
              iconPlacement="left"
              isDisabled={sending || !canSchedule}
            >
              {isScheduleMode ? L.applicant.comms.cancel_schedule : L.applicant.comms.schedule}
            </Button>
            <Button
              type="button"
              onClick={handleSend}
              variant="primary"
              size="md"
              icon={<Icon name="PaperAirplaneIcon" className="h-4 w-4" />}
              iconPlacement="left"
              isDisabled={sending || (!(selectedTemplate && templateMessage) && !replyMessage.trim()) || !selectedChannel || !availableChannels.length}
              loading={sending}
            >
              {L.applicant.comms.send}
            </Button>
          </div>
        </div>
      </div>

      {/* WhatsApp window closed warning (matching Comms.tsx) */}
      {whatsAppWindowClosed && (
        <Alert variant="warning" className="max-w-none">
          {L.applicant.comms.outside_whatsapp_window}
        </Alert>
      )}
    </div>
  );
};
